import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import DateInput from "../components/Forms/DateInput";
import { getStaffById, updateStaff } from "../../services/StaffService";
import { FIELD_LIMITS } from "../../utils/fieldLimits";

const EditStaff = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);

  const [formData, setFormData] = useState({
    employee_code: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    company_id: "",
    branch_id: "",
    department_id: "",
    sub_department_id: "",
    status: "Active",
    blood_group: "",
    date_of_birth: "",
    gender: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // ✅ Fetch Data
  const fetchBranches = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${user.corporateId}`);
      setBranches(res.data.data || res.data || []);
    } catch (err) {
      console.error("Branch fetch error:", err);
    }
  };

  const fetchDepartments = async (branchId = null) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const url = branchId
        ? `/settings/departments?corporate_id=${user.corporateId}&branch_id=${branchId}`
        : `/settings/departments?corporate_id=${user.corporateId}`;
      const res = await axiosInstance.get(url);
      const data = res.data.data || res.data || [];
      setDepartments(data);
      setFilteredDepartments(data);
    } catch (err) {
      console.error("Department fetch error:", err);
    }
  };

  const fetchSubDepartments = async (departmentId = null) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const url = departmentId
        ? `/settings/subDepartments?corporate_id=${user.corporateId}&department_id=${departmentId}`
        : `/settings/subDepartments?corporate_id=${user.corporateId}`;
      const res = await axiosInstance.get(url);
      setSubDepartments(res.data.data || res.data || []);
    } catch (err) {
      console.error("SubDepartment fetch error:", err);
    }
  };

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      const res = await getStaffById(id);
      const data = res.data.data || res.data;
      
      // Format date_of_birth if it exists
      let dateOfBirth = "";
      if (data.date_of_birth) {
        const date = new Date(data.date_of_birth);
        dateOfBirth = date.toISOString().split('T')[0];
      }
      
      setFormData({
        employee_code: data.employee_code || "",
        first_name: data.first_name || "",
        middle_name: data.middle_name || "",
        last_name: data.last_name || "",
        company_id: data.company_id?.toString() || "",
        branch_id: data.branch_id?.toString() || "",
        department_id: data.department_id?.toString() || "",
        sub_department_id: data.sub_department_id?.toString() || "",
        status: data.status || "Active",
        blood_group: data.blood_group || "",
        date_of_birth: dateOfBirth,
        gender: data.gender || "",
      });
      
      // Load dependent data
      if (data.branch_id) {
        await fetchDepartments(data.branch_id);
      }
      if (data.department_id) {
        await fetchSubDepartments(data.department_id);
      }
      
      setDataLoaded(true);
    } catch (err) {
      console.error("Staff fetch error:", err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to load staff data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filter departments when branch changes
  const handleBranchChange = async (e) => {
    const branchId = e.target.value;
    setFormData((prev) => ({ ...prev, branch_id: branchId, department_id: "", sub_department_id: "" }));
    await fetchDepartments(branchId);
    setSubDepartments([]);
  };

  const handleDepartmentChange = async (e) => {
    const departmentId = e.target.value;
    setFormData((prev) => ({ ...prev, department_id: departmentId, sub_department_id: "" }));
    await fetchSubDepartments(departmentId);
  };

  const isOnlySpecialChars = (str) => {
    const trimmed = (str || '').trim();
    if (!trimmed) return false;
    return !/[a-zA-Z0-9]/.test(trimmed);
  };

  const validate = () => {
    const errs = {};
    if (!formData.employee_code.trim()) errs.employee_code = "Employee code is required";
    else if (isOnlySpecialChars(formData.employee_code)) errs.employee_code = "Employee code cannot contain only special characters.";
    else if (formData.employee_code.length > FIELD_LIMITS.EMPLOYEE_CODE) errs.employee_code = `Employee code cannot exceed ${FIELD_LIMITS.EMPLOYEE_CODE} characters`;
    if (!formData.first_name.trim()) errs.first_name = "First name is required";
    else if (isOnlySpecialChars(formData.first_name)) errs.first_name = "First name cannot contain only special characters.";
    else if (formData.first_name.length > FIELD_LIMITS.STAFF_NAME) errs.first_name = `First name cannot exceed ${FIELD_LIMITS.STAFF_NAME} characters`;
    if (formData.middle_name.trim()) {
      if (isOnlySpecialChars(formData.middle_name)) errs.middle_name = "Middle name cannot contain only special characters.";
      else if (formData.middle_name.length > FIELD_LIMITS.STAFF_NAME) errs.middle_name = `Middle name cannot exceed ${FIELD_LIMITS.STAFF_NAME} characters`;
    }
    if (formData.last_name.trim()) {
      if (isOnlySpecialChars(formData.last_name)) errs.last_name = "Last name cannot contain only special characters.";
      else if (formData.last_name.length > FIELD_LIMITS.STAFF_NAME) errs.last_name = `Last name cannot exceed ${FIELD_LIMITS.STAFF_NAME} characters`;
    }
    if (!formData.company_id) errs.company_id = "Company is required";
    if (formData.date_of_birth) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (formData.date_of_birth >= todayStr) errs.date_of_birth = "Date of birth cannot be today or in the future.";
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      const payload = {
        employee_code: formData.employee_code.trim(),
        first_name: formData.first_name.trim(),
        middle_name: formData.middle_name.trim() || undefined,
        last_name: formData.last_name.trim() || undefined,
        company_id: parseInt(formData.company_id),
        branch_id: formData.branch_id ? parseInt(formData.branch_id) : undefined,
        department_id: formData.department_id ? parseInt(formData.department_id) : undefined,
        sub_department_id: formData.sub_department_id ? parseInt(formData.sub_department_id) : undefined,
        status: formData.status || "Active",
        blood_group: formData.blood_group || undefined,
        date_of_birth: formData.date_of_birth || undefined,
        gender: formData.gender || undefined,
      };
      
      await updateStaff(id, payload);
      toast.success("✅ Staff updated successfully!");
      setTimeout(() => navigate("/staff/staffList"), 1500);
    } catch (err) {
      console.error("Submit error:", err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || "Failed to update staff";
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const initData = async () => {
      await fetchBranches();
      await fetchDepartments();
      await fetchSubDepartments();
    };
    initData();
  }, []);

  useEffect(() => {
    if (id) {
      fetchStaffData();
    }
  }, [id]);

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const genders = ["Male", "Female", "Other"];
  const statuses = ["Active", "Inactive", "Resigned"];

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate("/staff/staffList")}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-4 mb-3">
              <label>Employee Code*</label>
              <input
                name="employee_code"
                value={formData.employee_code}
                onChange={handleChange}
                className={`form-control ${errors.employee_code ? 'is-invalid' : ''}`}
                placeholder="Enter employee code"
                maxLength={FIELD_LIMITS.EMPLOYEE_CODE}
              />
              {errors.employee_code && <div className="text-danger small">{errors.employee_code}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>First Name*</label>
              <input
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                placeholder="Enter first name"
                maxLength={FIELD_LIMITS.STAFF_NAME}
              />
              {errors.first_name && <div className="text-danger small">{errors.first_name}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Middle Name</label>
              <input
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
                className={`form-control ${errors.middle_name ? 'is-invalid' : ''}`}
                placeholder="Enter middle name (optional)"
                maxLength={FIELD_LIMITS.STAFF_NAME}
              />
              {errors.middle_name && <div className="text-danger small">{errors.middle_name}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Last Name</label>
              <input
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                placeholder="Enter last name (optional)"
                maxLength={FIELD_LIMITS.STAFF_NAME}
              />
              {errors.last_name && <div className="text-danger small">{errors.last_name}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select Gender</option>
                {genders.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Date of Birth</label>
              <DateInput
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className={`form-control ${errors.date_of_birth ? 'is-invalid' : ''}`}
                max={(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0]; })()}
              />
              {errors.date_of_birth && <div className="text-danger small">{errors.date_of_birth}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Blood Group</label>
              <select
                name="blood_group"
                value={formData.blood_group}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select Blood Group</option>
                {bloodGroups.map((bg) => (
                  <option key={bg} value={bg}>
                    {bg}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Status*</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`form-control ${errors.status ? 'is-invalid' : ''}`}
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {errors.status && <div className="text-danger small">{errors.status}</div>}
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Branch</label>
              <select
                name="branch_id"
                value={formData.branch_id}
                onChange={handleBranchChange}
                className="form-control"
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Department</label>
              <select
                name="department_id"
                value={formData.department_id}
                onChange={handleDepartmentChange}
                className="form-control"
                disabled={!formData.branch_id}
              >
                <option value="">Select Department</option>
                {filteredDepartments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4 mb-3">
              <label>Sub Department</label>
              <select
                name="sub_department_id"
                value={formData.sub_department_id}
                onChange={handleChange}
                className="form-control"
                disabled={!formData.department_id}
              >
                <option value="">Select Sub Department</option>
                {subDepartments.map((sd) => (
                  <option key={sd.id} value={sd.id}>
                    {sd.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2 w-100">
              <button 
                type="button"
                className="btn btn-cancel" 
                onClick={() => navigate("/staff/staffList")}
                disabled={isSubmitting}
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting} 
                className="btn user-form-btn sw-btn-next"
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                Update
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default EditStaff;
